import { type Locale, locales } from "./config";

/**
 * Stamped by the apps/marketing proxy on every upstream rewrite to the
 * app origin. apps/web answers any request *missing* it (i.e. direct
 * app.klarify.ca traffic) with `X-Robots-Tag: noindex`, keeping the app
 * host out of search as a duplicate of www. Fail-safe by construction:
 * losing the header can only leave the app host indexable (status quo),
 * never noindex the public site.
 *
 * Lives here (not @klarify/constants) because this module is bundled
 * into the marketing edge proxy, and @klarify/constants drags
 * @klarify/types#build (Prisma codegen) into the marketing zone's
 * Vercel build graph. apps/web/next.config.ts imports it from here for
 * its headers() rule rather than repeating the literal — safe because
 * this module's graph is a leaf (path -> config -> locales) with no
 * next-intl or Lingui runtime to pull into a config file.
 */
export const PUBLIC_ZONE_HEADER = "x-klarify-public-zone";

export const publicProfilePathPrefixes = ["/therapists", "/clinics"] as const;

export const marketingLocaleAppPathPrefixes = [
	...publicProfilePathPrefixes,
	"/blogs",
	"/unsubscribed",
] as const;

export function matchesPathnamePrefix(
	pathname: string,
	prefixes: readonly string[],
): boolean {
	return prefixes.some(
		(prefix) =>
			(prefix.endsWith("/") && pathname.startsWith(prefix)) ||
			(!prefix.endsWith("/") &&
				(pathname === prefix || pathname.startsWith(`${prefix}/`))),
	);
}

export function isPublicProfilePathname(pathname: string): boolean {
	return matchesPathnamePrefix(pathname, publicProfilePathPrefixes);
}

export function isMarketingLocaleAppPathname(pathname: string): boolean {
	return matchesPathnamePrefix(pathname, marketingLocaleAppPathPrefixes);
}

export function splitLocalePathname(pathname: string): {
	locale: Locale | null;
	pathname: string;
} {
	for (const locale of locales) {
		if (pathname === `/${locale}`) {
			return { locale, pathname: "/" };
		}
		if (pathname.startsWith(`/${locale}/`)) {
			return { locale, pathname: pathname.slice(locale.length + 1) };
		}
	}

	return { locale: null, pathname };
}

export function getPathnameWithoutLocale(pathname: string): string {
	return splitLocalePathname(pathname).pathname;
}

export function getLocalizedPathname(pathname: string): string {
	return stripLocalePathname(pathname);
}

export function getInternalLocalizedPathname(
	pathname: string,
	locale: string | null | undefined,
): string {
	if (
		!pathname.startsWith("/") ||
		pathname.startsWith("//") ||
		!locales.includes(locale as Locale)
	) {
		return pathname;
	}

	const match = /^([^?#]*)([?#].*)?$/.exec(pathname);
	const pathPart = match?.[1] ?? pathname;
	const suffix = match?.[2] ?? "";

	if (splitLocalePathname(pathPart).locale) {
		const { pathname: routePathname } = splitLocalePathname(pathPart);
		return `/${locale}${routePathname === "/" ? "" : routePathname}${suffix}`;
	}

	return `/${locale}${pathPart === "/" ? "" : pathPart}${suffix}`;
}

export function stripLocalePathname(pathname: string): string {
	if (!pathname.startsWith("/") || pathname.startsWith("//")) {
		return pathname;
	}

	const match = /^([^?#]*)([?#].*)?$/.exec(pathname);
	const pathPart = match?.[1] ?? pathname;
	const suffix = match?.[2] ?? "";
	const { locale, pathname: routePathname } = splitLocalePathname(pathPart);

	if (!locale) {
		return pathname;
	}

	return `${routePathname}${suffix}`;
}
